import React, { useState, useEffect } from 'react';
import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import { ATTACHMENT_STYLE_ENDPOINT } from '../config/api';
import { getChartCache, setChartCache, CHART_KEYS } from '../utils/cache';
import './AttachmentStyleChart.css';

const COLORS = {
  secure: '#86efac',   // light green
  anxious: '#fdba74',  // orangish
  avoidant: '#fca5a5', // pale red
};

// Standard SVG: 0° = right, 90° = down, 180° = left
function polarToCartesian(cx, cy, r, angleDeg) {
  const rad = (angleDeg * Math.PI) / 180;
  return {
    x: cx + r * Math.cos(rad),
    y: cy + r * Math.sin(rad),
  };
}

/** Sample attachment data for new users: same shape as API (pct_secure, pct_anxious, pct_avoidant, attachment_style). */
function getSampleAttachmentData() {
  return {
    pct_secure: 50,
    pct_anxious: 30,
    pct_avoidant: 20,
    attachment_style: 'Secure',
  };
}

function AttachmentStyleChart() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hover, setHover] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        const user = await getCurrentUser();
        const uid = user?.userId;
        const cached = uid ? getChartCache(CHART_KEYS.attachment, uid) : null;
        if (!cancelled && cached != null && (cached.pct_secure !== undefined || cached.attachment_style != null)) {
          setData(cached);
          setLoading(false);
        }
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();
        const res = await fetch(ATTACHMENT_STYLE_ENDPOINT, {
          headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
        });
        if (!res.ok) throw new Error('Failed to load attachment style');
        const json = await res.json();
        const payload = json?.statusCode != null ? JSON.parse(json.body || '{}') : json;
        const parsed = payload && (payload.pct_secure !== undefined || payload.attachment_style != null) ? payload : null;
        if (!cancelled) {
          setData(parsed);
          setError(null);
          if (uid && parsed) setChartCache(CHART_KEYS.attachment, uid, parsed);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error loading attachment style:', err);
          setError(err.message);
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="attachment-style-chart-container">
        <div className="attachment-style-chart-loading">Loading attachment style...</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="attachment-style-chart-container">
        <div className="attachment-style-chart-error">Unable to load attachment style</div>
      </div>
    );
  }
  const hasRealData = data && (data.total_count === undefined || data.total_count > 0);
  const effectiveData = hasRealData ? data : getSampleAttachmentData();

  const pctSecure = Number(effectiveData.pct_secure) || 0;
  const pctAnxious = Number(effectiveData.pct_anxious) || 0;
  const pctAvoidant = Number(effectiveData.pct_avoidant) || 0;
  const total = pctSecure + pctAnxious + pctAvoidant;
  const scale = total > 0 ? 180 / total : 0;

  // Top semicircle (facing up): arc from 180° (left) through 270° (top) to 360° (right). Angle INCREASES.
  // Order left → right: Secure → Anxious → Avoidant. Overall size 20% larger, ring thickness unchanged.
  const cx = 100;
  const cy = 83;   // position down
  const R = 51 * 1.2;   // 61.2 – outer radius 20% larger
  const r = R - (51 - 28); // inner: same ring width (23) as before
  const viewHeight = cy + r;

  let angle = 180; // start at left
  const segments = [
    { key: 'secure', pct: pctSecure, color: COLORS.secure, label: 'Secure' },
    { key: 'anxious', pct: pctAnxious, color: COLORS.anxious, label: 'Anxious' },
    { key: 'avoidant', pct: pctAvoidant, color: COLORS.avoidant, label: 'Avoidant' },
  ]
    .filter((s) => s.pct > 0)
    .map((s) => {
      const startAngle = angle;
      angle += s.pct * scale; // walk top semicircle left→right: 180 → 360
      const endAngle = angle;
      return { ...s, startAngle, endAngle };
    });

  // One slice: short arcs only (large-arc 0). Top semicircle = angle increasing 180→360.
  // Outer: start→end along top = sweep 1. Inner: end→start along top = sweep 0.
  const segmentPath = (seg) => {
    const oStart = polarToCartesian(cx, cy, R, seg.startAngle);
    const oEnd = polarToCartesian(cx, cy, R, seg.endAngle);
    const iStart = polarToCartesian(cx, cy, r, seg.startAngle);
    const iEnd = polarToCartesian(cx, cy, r, seg.endAngle);
    return (
      `M ${oStart.x} ${oStart.y} ` +
      `A ${R} ${R} 0 0 1 ${oEnd.x} ${oEnd.y} ` +
      `L ${iEnd.x} ${iEnd.y} ` +
      `A ${r} ${r} 0 0 0 ${iStart.x} ${iStart.y} Z`
    );
  };

  // Middle of each segment: angular midpoint + radius slightly inward so label sits lower in the arc
  const labelRadius = r + (R - r) * 0.4;
  const segmentLabelPos = (seg) => {
    const midAngle = (seg.startAngle + seg.endAngle) / 2;
    return polarToCartesian(cx, cy, labelRadius, midAngle);
  };

  const emptySemicirclePath = () => {
    const left = polarToCartesian(cx, cy, R, 180);
    const right = polarToCartesian(cx, cy, R, 360);
    const rightInner = polarToCartesian(cx, cy, r, 360);
    const leftInner = polarToCartesian(cx, cy, r, 180);
    return (
      `M ${left.x} ${left.y} ` +
      `A ${R} ${R} 0 0 1 ${right.x} ${right.y} ` +
      `L ${rightInner.x} ${rightInner.y} ` +
      `A ${r} ${r} 0 0 0 ${leftInner.x} ${leftInner.y} Z`
    );
  };

  const pct = (v) => (typeof v === 'number' && !Number.isNaN(v) ? String(Math.round(v)) : '0');

  return (
    <div
      className="attachment-style-chart-container"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {!hasRealData && (
        <div className="chart-cta-new-user" role="status">
          <p className="chart-cta-new-user-headline">Start journalling to see your data.</p>
          <p className="chart-cta-new-user-note">This is a sample view.</p>
        </div>
      )}
      <div className="attachment-style-chart-header">
        {effectiveData.attachment_style ? (
          <span className="attachment-style-chart-title">{effectiveData.attachment_style}</span>
        ) : (
          <span className="attachment-style-chart-title attachment-style-chart-title-muted">Attachment style</span>
        )}
      </div>
      <div className="attachment-style-chart-body">
        <svg className="attachment-style-chart-svg" viewBox={`0 0 200 ${viewHeight}`} preserveAspectRatio="xMidYMid meet">
          <defs>
            <filter id="attachment-arc-shadow" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="0" dy="1" stdDeviation="0.5" floodOpacity="0.15" />
            </filter>
          </defs>
          {segments.length === 0 ? (
            <path
              d={emptySemicirclePath()}
              fill="#e5e7eb"
              stroke="none"
            />
          ) : (
            <>
              {segments.map((seg) => (
                <path
                  key={seg.key}
                  d={segmentPath(seg)}
                  fill={seg.color}
                  stroke="rgba(255,255,255,0.9)"
                  strokeWidth="2"
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  style={{ filter: 'url(#attachment-arc-shadow)' }}
                />
              ))}
              {segments.map((seg) => {
                const pos = segmentLabelPos(seg);
                return (
                  <text
                    key={`label-${seg.key}`}
                    x={pos.x}
                    y={pos.y}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    className="attachment-style-chart-segment-pct"
                  >
                    {pct(seg.pct)}%
                  </text>
                );
              })}
            </>
          )}
        </svg>
        <div className="attachment-style-chart-legend">
          <div className={`attachment-style-chart-legend-item${pctSecure === 0 ? ' attachment-style-chart-legend-item-muted' : ''}`}>
            <span className="attachment-style-chart-legend-dot" style={{ background: COLORS.secure }} />
            <span>Secure</span>
          </div>
          <div className={`attachment-style-chart-legend-item${pctAnxious === 0 ? ' attachment-style-chart-legend-item-muted' : ''}`}>
            <span className="attachment-style-chart-legend-dot" style={{ background: COLORS.anxious }} />
            <span>Anxious</span>
          </div>
          <div className={`attachment-style-chart-legend-item${pctAvoidant === 0 ? ' attachment-style-chart-legend-item-muted' : ''}`}>
            <span className="attachment-style-chart-legend-dot" style={{ background: COLORS.avoidant }} />
            <span>Avoidant</span>
          </div>
        </div>
        {hover && (
          <div className="attachment-style-chart-tooltip">
            <div className="attachment-style-chart-tooltip-row">
              <span className="attachment-style-chart-tooltip-dot" style={{ background: COLORS.secure }} />
              <span>Secure</span>
              <span className="attachment-style-chart-tooltip-pct">{pct(pctSecure)}%</span>
            </div>
            <div className="attachment-style-chart-tooltip-row">
              <span className="attachment-style-chart-tooltip-dot" style={{ background: COLORS.anxious }} />
              <span>Anxious</span>
              <span className="attachment-style-chart-tooltip-pct">{pct(pctAnxious)}%</span>
            </div>
            <div className="attachment-style-chart-tooltip-row">
              <span className="attachment-style-chart-tooltip-dot" style={{ background: COLORS.avoidant }} />
              <span>Avoidant</span>
              <span className="attachment-style-chart-tooltip-pct">{pct(pctAvoidant)}%</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AttachmentStyleChart;
